import { RACSearchDropdown } from '@rentacenter/racstrap';
import { get } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  ClassificationsInputPlaceholder,
  emptyField,
  PoFormField,
} from '../../constants/constants';
import { ClassificationType } from '../../domain/PurchaseOrder/ClassificationType';
import { PoStatus } from '../../domain/PurchaseOrder/PoStatus';
import { SelectedFieldOption } from '../../types/types';
import { filterById, populateSelectByChild } from '../../utils/utils';
import {
  useClassificationsDispatch,
  useClassificationsState,
} from './context/Provider';
import { getClassificationFieldName } from './context/fieldNames';
import { ClassificationsComponentProps } from './context/types';

export const subTypeTestId = 'subTypeComponent';

// eslint-disable-next-line
export const SubType = (props: ClassificationsComponentProps) => {
  const { name, label, purchaseOrder, qrScannerOutput, showErrorMessage } =
    props;
  const { errors, setValue } = useFormContext();
  const { subTypes, startWithTypes } = useClassificationsState();
  const dispatch = useClassificationsDispatch();
  const [itemValue, setItemValue] = useState(emptyField);
  const [isViewMode, setIsViewMode] = useState(false);
  const getFormFieldName = useCallback(
    (fieldName: PoFormField) => getClassificationFieldName(name, fieldName),
    [name]
  );

  // eslint-disable-next-line
  useEffect(() => {
    const poItemSubType = purchaseOrder && purchaseOrder.itemSubType;

    const selectedSubType: {
      name: string;
      id: string;
      subTypes: [];
    } | null = poItemSubType ? filterById(subTypes, poItemSubType)[0] : null;

    if (subTypes.length === 1) {
      setItemValue({ value: subTypes[0].id, label: subTypes[0].name });

      const itemNumber = subTypes[0]?.items?.[0]?.itemNumber ?? 0;

      setValue(PoFormField?.RmsItemNumber, itemNumber, {
        shouldValidate: true,
        shouldDirty: true,
      });

      if (startWithTypes) {
        dispatch({
          type: ClassificationType.SET_SINGLE_DEPARTMENT,
          payload: subTypes[0].departments,
        });
      }
    } else {
      setItemValue({
        value: (selectedSubType && selectedSubType.id) || '',
        label:
          (selectedSubType && selectedSubType.name) ||
          ClassificationsInputPlaceholder,
      });

      if (startWithTypes) {
        dispatch({
          type: ClassificationType.CLEAR_DEPARTMENTS,
        });
        if (selectedSubType) {
          dispatch({
            type: ClassificationType.SET_DEPARTMENTS,
            payload: filterById(subTypes, selectedSubType.id)[0].departments,
          });
        }
      }

      !startWithTypes &&
        selectedSubType &&
        dispatch({
          type: ClassificationType.SET_SUBTYPES,
          payload: selectedSubType.subTypes,
        });
    }
    if (qrScannerOutput) {
      const selectedItem = subTypes?.find(
        (o) => o.id == qrScannerOutput.subTypeCode
      );
      setItemValue({
        value: selectedItem?.id
          ? selectedItem?.id
          : qrScannerOutput?.subTypeCode
          ? qrScannerOutput.subTypeCode
          : '',
        label: selectedItem?.name
          ? selectedItem?.name
          : qrScannerOutput?.subType
          ? qrScannerOutput?.subType
          : '',
      });
      setValue(
        getFormFieldName(PoFormField.ItemSubType),
        selectedItem?.id ? selectedItem?.id : qrScannerOutput?.subTypeCode,
        {
          shouldValidate: true,
          shouldDirty: true,
        }
      );
      setIsViewMode(
        qrScannerOutput?.isScannedCategoryKept
          ? qrScannerOutput?.isScannedCategoryKept
          : false
      );
      if (!qrScannerOutput.departmentNumber) {
        setItemValue(emptyField);
      }
      // }
    }
    // eslint-disable-next-line
  }, [subTypes, dispatch, startWithTypes,qrScannerOutput]);

  // Flag if dashboard-sent value is not found in classification options
  useEffect(() => {
    if (qrScannerOutput || !subTypes?.length) return;
    const prefillValue = purchaseOrder?.itemSubType;
    if (!prefillValue) return;
    const resolved = subTypes.some((st: any) => st.id == prefillValue);
    setTimeout(
      () => setValue(name + '__unresolved', resolved ? '' : 'true'),
      0
    );
    // eslint-disable-next-line
  }, [subTypes, qrScannerOutput]);

  const handleChange = useCallback(
    (selected: SelectedFieldOption) => {
      const { value, label } = selected;
      setItemValue({ value, label });
      setValue(name, value, { shouldValidate: true, shouldDirty: true });

      const selectedSubType = subTypes.find((subType) => subType.id === value);

      const itemNumber = selectedSubType?.items?.[0]?.itemNumber ?? 0;

      setValue(PoFormField?.RmsItemNumber, itemNumber, {
        shouldValidate: true,
        shouldDirty: true,
      });

      if (startWithTypes) {
        const filterdDepartments = filterById(subTypes, value)[0].departments;
        let singleSubDepartment = filterdDepartments[0].subDepartments;
        let singleBracket = singleSubDepartment[0].brackets;

        if (purchaseOrder && purchaseOrder.itemSubType !== value) {
          purchaseOrder.department = '';
          purchaseOrder.subDepartment = '';
          purchaseOrder.bracket = '';
        }
        dispatch({
          type: ClassificationType.SET_DEPARTMENTS,
          payload: filterdDepartments,
        });
        if (filterdDepartments.length === 1) {
          setValue(
            getFormFieldName(PoFormField.Department),
            filterdDepartments[0].id,
            {
              shouldValidate: true,
              shouldDirty: true,
            }
          );
        } else {
          setValue(getFormFieldName(PoFormField.Department), '', {
            shouldValidate: false,
            shouldDirty: true,
          });
        }

        if (singleSubDepartment.length === 1) {
          setValue(
            getFormFieldName(PoFormField.SubDepartment),
            singleSubDepartment[0].id,
            {
              shouldValidate: true,
              shouldDirty: true,
            }
          );
        } else {
          singleSubDepartment = [];
          setValue(getFormFieldName(PoFormField.SubDepartment), '', {
            shouldValidate: false,
            shouldDirty: true,
          });
        }
        if (singleBracket.length === 1) {
          setValue(getFormFieldName(PoFormField.Bracket), singleBracket[0].id, {
            shouldValidate: true,
            shouldDirty: true,
          });
        } else {
          singleBracket = [];
          setValue(getFormFieldName(PoFormField.Bracket), '', {
            shouldValidate: false,
            shouldDirty: true,
          });
        }
        dispatch({
          type: ClassificationType.SET_SUBTYPES,
          payload: filterById(subTypes, value)[0].departments,
        });
      }
    },
    [
      dispatch,
      getFormFieldName,
      purchaseOrder,
      name,
      setValue,
      startWithTypes,
      subTypes,
    ]
  );

  useEffect(
    // eslint-disable-next-line
    () => {
      setIsViewMode(
        purchaseOrder
          ? purchaseOrder?.poStatus === PoStatus.SUBMITTED ||
              purchaseOrder.poStatus === PoStatus.OPEN_CANCELLED
          : false
      );
    },
    [purchaseOrder]
  );

  return useMemo(
    () => (
      <Controller
        name={name}
        rules={{ required: true }}
        render={({ name }) => (
          <RACSearchDropdown
            name={name}
            inputLabel={label}
            options={populateSelectByChild(subTypes) || []}
            OnChange={handleChange}
            required
            disabled={isViewMode || !subTypes.length}
            value={itemValue}
            errormessage={
              showErrorMessage && get(errors, name)
                ? `${label} is required`
                : ''
            }
          />
        )}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      errors,
      isViewMode,
      itemValue,
      label,
      name,
      purchaseOrder,
      showErrorMessage,
      subTypes,
    ]
  );
};
